import { markOwnedReadCancellation } from '../lib/actionFailureSignals';

export type TimeoutAbortableLogEvent = {
  event: 'timeout' | 'abort';
  key?: string;
  correlationId?: string;
  timeoutMs?: number;
  durationMs?: number;
  error?: unknown;
};

export type TimeoutAbortableLogger = (entry: TimeoutAbortableLogEvent) => void;

export type TimeoutAbortableOptions = {
  timeoutMs: number;
  key?: string;
  correlationId?: string;
  logger?: TimeoutAbortableLogger;
  signal?: AbortSignal | null;
  operation?: 'read' | 'mutation';
};

export type TimeoutAbortableContext = {
  signal: AbortSignal | null;
};

export class TimeoutError extends Error {
  readonly timeoutMs: number;
  readonly key?: string;
  readonly correlationId?: string;

  constructor(message: string, params: { timeoutMs: number; key?: string; correlationId?: string }) {
    super(message);
    this.name = 'TimeoutError';
    this.timeoutMs = params.timeoutMs;
    this.key = params.key;
    this.correlationId = params.correlationId;
  }
}

export class AbortError extends Error {
  readonly key?: string;
  readonly correlationId?: string;
  readonly cause?: unknown;

  constructor(message: string, params?: { key?: string; correlationId?: string; cause?: unknown }) {
    super(message);
    this.name = 'AbortError';
    this.key = params?.key;
    this.correlationId = params?.correlationId;
    this.cause = params?.cause;
  }
}

/**
 * Returns true if the error is an abort/cancellation (e.g. request cancelled by React Query
 * when queryKey changes or component unmounts). Aborts should not be shown as "Failed to load items".
 * Checks: AbortError instance, name, code 20 (DOMException.ABORT_ERR), and message fallback
 * so we still recognize aborts even if the error was wrapped or lost its prototype (e.g. after serialization).
 */
export const isAbortLikeError = (error: unknown): boolean => {
  if (!error) return false;
  if (error instanceof AbortError) return true;
  const named = (error as { name?: unknown })?.name;
  if (named === 'AbortError') return true;
  const errorCode =
    (error as { errorCode?: unknown })?.errorCode ??
    (error as { code?: unknown })?.code;
  if (errorCode === 20 || errorCode === '20') return true;
  const msg = typeof (error as { message?: unknown })?.message === 'string'
    ? (error as { message: string }).message
    : '';
  if (msg.length > 0) {
    if (msg.includes('AbortError') || /signal is aborted|request aborted|abort/i.test(msg)) return true;
  }
  return false;
};

const resolveTimeoutMs = (timeoutMs: number): number => {
  if (!Number.isFinite(timeoutMs)) return 0;
  return Math.max(1, Math.trunc(timeoutMs));
};

const resolveLateResultEventName = (key?: string, correlationId?: string): string => {
  if (typeof key === 'string' && key.startsWith('order-context')) return 'orderContext';
  if (typeof correlationId === 'string' && correlationId.startsWith('event-lines')) return 'eventLines';
  return 'request';
};

export async function withTimeoutAbortable<T>(
  promiseFactory: (context: TimeoutAbortableContext) => Promise<T>,
  options: TimeoutAbortableOptions,
): Promise<T> {
  const timeoutMs = resolveTimeoutMs(options.timeoutMs);
  if (options.signal?.aborted) {
    const error = new AbortError('Request aborted before start', {
      key: options.key,
      correlationId: options.correlationId,
    });
    if (options.operation === 'read') markOwnedReadCancellation(error);
    throw error;
  }

  const startedAt = Date.now();
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const signal = controller?.signal ?? null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let timedOut = false;
  let settled = false;
  let timeoutError: TimeoutError | null = null;
  let lateResultLogged = false;
  let externalAbortListener: (() => void) | null = null;
  const resolveTimeoutDurationMs = (elapsedMs: number): number => {
    if (!timeoutMs) return 0;
    return Math.max(0, Math.min(timeoutMs, elapsedMs));
  };

  if (options.signal && controller) {
    externalAbortListener = () => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    };
    options.signal.addEventListener('abort', externalAbortListener, { once: true });
  }

  const markTimeout = (durationMs: number): TimeoutError => {
    if (!timeoutError) {
      timeoutError = new TimeoutError(`Timed out after ${timeoutMs}ms`, {
        timeoutMs,
        key: options.key,
        correlationId: options.correlationId,
      });
      options.logger?.({
        event: 'timeout',
        key: options.key,
        correlationId: options.correlationId,
        timeoutMs,
        durationMs: resolveTimeoutDurationMs(durationMs),
        error: timeoutError,
      });
    }
    timedOut = true;
    settled = true;
    return timeoutError;
  };

  const logLateResultIgnored = (elapsedMs: number) => {
    if (lateResultLogged) return;
    lateResultLogged = true;
    const eventName = `${resolveLateResultEventName(options.key, options.correlationId)}.late_result_ignored`;
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug({
        event: eventName,
        correlationId: options.correlationId,
        elapsedMs,
        key: options.key,
      });
    }
  };

  const ignoreLateResult = (elapsedMs: number) => {
    // Ignore late results so post-timeout completions never crash or mutate caller state.
    logLateResultIgnored(elapsedMs);
    return new Promise<never>(() => {});
  };

  const timeoutPromise = new Promise<never>((_, reject) => {
    if (!timeoutMs) return;
    timeoutId = setTimeout(() => {
      if (settled) return;
      if (controller && !controller.signal.aborted) {
        controller.abort();
      }
      reject(markTimeout(Date.now() - startedAt));
    }, timeoutMs);
  });

  const requestPromise = Promise.resolve().then(() => promiseFactory({ signal }));
  const guardedRequestPromise = requestPromise.then(
    (result) => {
      if (timedOut) {
        return ignoreLateResult(Date.now() - startedAt);
      }
      if (timeoutMs) {
        const elapsed = Date.now() - startedAt;
        if (elapsed >= timeoutMs) {
          throw markTimeout(elapsed);
        }
      }
      settled = true;
      return result;
    },
    (error) => {
      if (timedOut) {
        return ignoreLateResult(Date.now() - startedAt);
      }
      if (timeoutMs) {
        const elapsed = Date.now() - startedAt;
        if (elapsed >= timeoutMs) {
          throw markTimeout(elapsed);
        }
      }
      settled = true;
      throw error;
    },
  );

  try {
    if (!timeoutMs) return await requestPromise;
    return await Promise.race([guardedRequestPromise, timeoutPromise]);
  } catch (error) {
    if (!timedOut && signal?.aborted) {
      if (options.operation === 'read' && options.signal?.aborted) markOwnedReadCancellation(error);
      // Rethrow original if already abort-like so we don't lose identity (e.g. DOMException or name/code).
      if (error instanceof AbortError) {
        options.logger?.({
          event: 'abort',
          key: options.key,
          correlationId: options.correlationId,
          durationMs: Date.now() - startedAt,
          error,
        });
        throw error;
      }
      if (isAbortLikeError(error)) {
        options.logger?.({
          event: 'abort',
          key: options.key,
          correlationId: options.correlationId,
          durationMs: Date.now() - startedAt,
          error,
        });
        throw error;
      }
      const abortError = new AbortError('Request aborted', {
        key: options.key,
        correlationId: options.correlationId,
        cause: error,
      });
      if (options.operation === 'read' && options.signal?.aborted) markOwnedReadCancellation(abortError);
      options.logger?.({
        event: 'abort',
        key: options.key,
        correlationId: options.correlationId,
        durationMs: Date.now() - startedAt,
        error: abortError,
      });
      throw abortError;
    }
    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (options.signal && externalAbortListener && typeof options.signal.removeEventListener === 'function') {
      options.signal.removeEventListener('abort', externalAbortListener);
    }
  }
}
